import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Check } from 'lucide-react';
import FloatingCube from './FloatingCube';
import {
  INBOUND_ENABLED,
  WorkspaceId,
  getStrongWorkspaceForPath,
  useWorkspace,
} from '../layout/WorkspaceContext';

interface WorkspaceSwitcherProps {
  collapsed: boolean;
  /**
   * When the sidebar is collapsed, clicking the cube should simply request
   * that the sidebar expand; the actual workspace dropdown is only shown
   * in the expanded state.
   */
  onRequestExpand?: () => void;
}

type CubeVariant = 'workspace-outbound' | 'workspace-inbound';

const WORKSPACE_META: Record<
  WorkspaceId,
  { label: string; description: string; cubeVariant: CubeVariant; defaultPath: string }
> = {
  outbound: {
    label: 'Outbound – Sales',
    description: 'Campaigns, sequences, AI calls',
    cubeVariant: 'workspace-outbound',
    defaultPath: '/app/outbound/quick-call',
  },
  inbound: {
    label: 'Inbound – Reception',
    description: 'AI reception, voicemail, call routing',
    cubeVariant: 'workspace-inbound',
    defaultPath: '/app/inbound/launchpad',
  },
};

const WorkspaceSwitcher: React.FC<WorkspaceSwitcherProps> = ({
  collapsed,
  onRequestExpand,
}) => {
  const { workspace, setWorkspace } = useWorkspace();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (event: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // If inbound is disabled, we don't show a multi-workspace switcher at all.
  if (!INBOUND_ENABLED) {
    return null;
  }

  const activeMeta = WORKSPACE_META[workspace];

  const handleSelect = (next: WorkspaceId) => {
    if (next === workspace) {
      setOpen(false);
      return;
    }

    setWorkspace(next);

    const currentStrong = getStrongWorkspaceForPath(location.pathname);
    const targetMeta = WORKSPACE_META[next];

    // Only auto-navigate when switching away from a path that clearly belongs
    // to the other workspace. Neutral/shared paths keep the user in place.
    if (currentStrong && currentStrong !== next) {
      navigate(targetMeta.defaultPath);
    }

    setOpen(false);
  };

  const handleButtonClick = () => {
    if (collapsed) {
      // In collapsed mode, treat the cube as a shortcut to expand the sidebar
      // and let the full workspace switcher live in the expanded chrome.
      onRequestExpand?.();
      return;
    }
    setOpen((prev) => !prev);
  };

  return (
    <div ref={containerRef} className="relative mb-4">
      <button
        type="button"
        onClick={handleButtonClick}
        className={`flex h-12 w-full items-center transition-colors ${
          collapsed
            ? ''
            : 'rounded-btn bg-neutral-50/70 ring-1 ring-inset ring-neutral-90/10 hover:bg-neutral-100/80'
        }`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="flex h-12 w-[70px] shrink-0 items-center justify-center">
          <FloatingCube variant={activeMeta.cubeVariant} />
        </span>
        {!collapsed && (
          <>
            <div className="flex min-w-0 flex-col text-left">
              <span className="text-[10px] uppercase tracking-wide text-neutral-400">
                Workspace
              </span>
              <span className="truncate text-sm font-medium text-neutral-900">
                {activeMeta.label}
              </span>
            </div>
            <motion.div
              animate={{ rotate: open ? 180 : 0 }}
              transition={{ duration: 0.18 }}
              className="ml-auto mr-3"
            >
              <ChevronDown
                size={14}
                className="text-neutral-400 group-hover:text-neutral-700"
              />
            </motion.div>
          </>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.98 }}
            transition={{ duration: 0.16, ease: [0.2, 0, 0, 1] }}
            className="absolute left-0 right-0 z-50 mt-2 rounded-xl border border-neutral-90/10 bg-white shadow-lg shadow-neutral-900/5"
          >
            {(Object.keys(WORKSPACE_META) as WorkspaceId[]).map((id) => {
              const meta = WORKSPACE_META[id];
              const isActive = id === workspace;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => handleSelect(id)}
                  className={`flex w-full items-center px-3 py-2.5 text-left transition-colors first:rounded-t-xl last:rounded-b-xl ${
                    isActive
                      ? 'bg-neutral-90/5'
                      : 'hover:bg-neutral-50/90 active:bg-neutral-100'
                  }`}
                >
                  <div className="mr-3 shrink-0">
                    <FloatingCube variant={meta.cubeVariant} />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-neutral-900">
                      {meta.label}
                    </span>
                    <span className="text-xs text-neutral-500">
                      {meta.description}
                    </span>
                  </div>
                  {isActive && (
                    <Check className="ml-auto h-4 w-4 text-primary-40" />
                  )}
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default WorkspaceSwitcher;


